/**
 * All Filters Dialog
 *
 * A dialog that displays all available filters in a vertical scrollable list.
 * Used from the landing page's search bar to configure filters before searching.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import type { SearchFilters } from '../../types/search';
import { useFacets, getFacetValues } from '../../hooks/useFacets';
import { isFacetValueDisabled } from '../../utils/cooccurrenceIndex';

interface AllFiltersDialogProps {
  open: boolean;
  onClose: () => void;
  filters: SearchFilters;
  onChange: (filters: SearchFilters) => void;
}

export function AllFiltersDialog({ open, onClose, filters, onChange }: AllFiltersDialogProps) {
  const { t } = useTranslation('search');
  const { facets, loading, cooccurrenceIndex } = useFacets();
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 0);
  }, []);

  const toggleValue = useCallback(
    (fieldName: string, value: string) => {
      const current = (filters[fieldName as keyof SearchFilters] as string[] | undefined) ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      onChange({
        ...filters,
        [fieldName]: next.length > 0 ? next : undefined,
      });
    },
    [filters, onChange]
  );

  const selectAll = useCallback(
    (fieldName: string, values: string[]) => {
      onChange({
        ...filters,
        [fieldName]: values,
      });
    },
    [filters, onChange]
  );

  const clearAll = useCallback(() => {
    onChange({});
  }, [onChange]);

  const activeCount = facets.reduce(
    (sum, field) => sum + ((filters[field.field_name as keyof SearchFilters] as string[] | undefined)?.length ?? 0),
    0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" component="span">
          {t('filters.title')}
          {activeCount > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({t('filters.selected', { count: activeCount })})
            </Typography>
          )}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider
        sx={{
          position: 'relative',
          zIndex: 1,
          boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.5)' : 'none',
          transition: 'box-shadow 0.2s ease-in-out',
        }}
      />

      {/* Scrollable Content */}
      <DialogContent onScroll={handleScroll} sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        {loading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Skeleton variant="text" width={120} height={28} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={40} sx={{ mb: 0.5 }} />
                <Skeleton variant="rounded" height={40} sx={{ mb: 0.5 }} />
                <Skeleton variant="rounded" height={40} />
              </Box>
            ))}
          </Box>
        ) : (
          facets.map((field, index) => {
            const selected = (filters[field.field_name as keyof SearchFilters] as string[] | undefined) ?? [];
            const sortedValues = getFacetValues(field);
            const allValues = sortedValues.map((v) => v.value);
            const facetDisplayName = t(`facets.${field.field_name}`, { defaultValue: field.display_name });

            return (
              <Box key={field.field_name} sx={{ mb: index < facets.length - 1 ? 3 : 0 }}>
                {/* Section Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.primary">
                    {facetDisplayName}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => selectAll(field.field_name, allValues)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {t('filters.selectAll')}
                  </Button>
                </Box>

                {/* Filter Options */}
                <Box
                  sx={{
                    bgcolor: 'surfaceContainerLow',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  {sortedValues.map((facetValue) => {
                    const checked = selected.includes(facetValue.value);
                    const disabled =
                      !checked &&
                      isFacetValueDisabled(
                        cooccurrenceIndex,
                        field.field_name,
                        facetValue.value,
                        filters as Record<string, readonly string[] | undefined>,
                      );
                    // Prefer i18n translation for the facet value (e.g. source, source_type),
                    // fall back to language-specific key, then backend-provided display_name.
                    const translatedValue = t(
                      `facets.values.${field.field_name}.${facetValue.value}`,
                      { defaultValue: '' }
                    );
                    const valueDisplayName = translatedValue
                      ? translatedValue
                      : field.field_name === 'language'
                        ? t(`languages.${facetValue.value}`, { defaultValue: facetValue.display_name })
                        : facetValue.display_name;
                    return (
                      <MenuItem
                        key={facetValue.value}
                        dense
                        disabled={disabled}
                        onClick={() => toggleValue(field.field_name, facetValue.value)}
                        sx={{
                          '&:not(:last-child)': {
                            //borderBottom: '1px solid',
                            borderColor: 'divider',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            size="small"
                            checked={checked}
                            disableRipple
                            tabIndex={-1}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={valueDisplayName}
                          slotProps={{
                            primary: { variant: 'body2' },
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>

      <Divider />

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          size="small"
          onClick={clearAll}
          disabled={activeCount === 0}
          sx={{ textTransform: 'none' }}
        >
          {t('filters.clearAll')}
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
        >
          {t('filters.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
